<template>
  <div class="max-w-2xl mx-auto space-y-12 pb-16">
    <!-- Profile -->
    <section>
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Profile</h1>

      <div class="mt-6 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 dark:border-white/10 dark:bg-gray-900/60 flex flex-col sm:flex-row items-center sm:items-start gap-6 shadow-xs">
        <img
          class="size-24 rounded-full bg-gray-50 outline -outline-offset-1 outline-black/5 shadow-inner dark:bg-gray-800 dark:outline-white/10"
          :src="gravatarUrl(auth.username, { size: 256, defaultImage: 'retro' })"
          :alt="`${auth.displayName} avatar`"
        />
        <div class="flex-1 text-center sm:text-left">
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">{{ auth.displayName }}</h2>
          <p class="text-base text-gray-500 dark:text-gray-400">{{ auth.username }}</p>
          <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-4">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                  :class="flavorBadge.classes">
              {{ flavorBadge.label }}
            </span>
            <span
              v-if="auth.emailVerified !== null"
              class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
              :class="
                auth.emailVerified
                  ? 'bg-green-50 text-green-700 inset-ring inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20'
                  : 'bg-amber-50 text-amber-800 inset-ring inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:inset-ring-amber-400/20'
              "
              data-testid="email-status"
            >
              {{ auth.emailVerified ? 'Email verified' : 'Email not verified' }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="auth.emailVerified === false"
        id="email-verification"
        class="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-6 dark:border-amber-500/30 dark:bg-amber-500/10"
      >
        <p class="text-sm font-semibold text-amber-900 dark:text-amber-200">Verify your email address</p>
        <p class="mt-1 text-sm text-amber-800 dark:text-amber-300">
          We sent a 6-digit code to <strong class="break-all">{{ auth.username }}</strong>.
          You need a verified address to create access tokens<template v-if="canManageBilling">
            and to choose a plan for {{ organizationName }}</template
          >.
        </p>
        <div class="mt-3">
          <EmailVerificationForm />
        </div>
      </div>
    </section>

    <!-- Security Section -->
    <section>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Security Settings</h2>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Update your password to keep your account secure.</p>

      <div
        class="mt-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
      >
      <form class="space-y-4" @submit.prevent="handleFormSubmit">
        <div v-if="!mfaDisplay" class="space-y-4">
          <div>
            <label
              for="currentPassword"
              class="block text-sm font-medium text-gray-900 dark:text-white"
              >Current password</label
            >
            <input
              id="currentPassword"
              name="currentPassword"
              type="password"
              autocomplete="current-password"
              required
              v-model="currentPassword"
              :disabled="loading"
              class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label for="newPassword" class="block text-sm font-medium text-gray-900 dark:text-white"
              >New password</label
            >
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              autocomplete="new-password"
              required
              v-model="newPassword"
              :disabled="loading"
              class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Enter new password"
            />
            <p
              v-if="newPassword && !isPasswordValid"
              class="mt-1 text-sm text-red-600 dark:text-red-400"
              role="alert"
            >
              Password must be at least 8 characters and include uppercase, lowercase, number, and
              symbol.
            </p>
          </div>
          <div>
            <label
              for="confirmPassword"
              class="block text-sm font-medium text-gray-900 dark:text-white"
              >Confirm new password</label
            >
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autocomplete="new-password"
              required
              v-model="confirmPassword"
              :disabled="loading"
              class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Re-enter new password"
            />
            <p
              v-if="newPassword && confirmPassword && !passwordsMatch"
              class="mt-1 text-sm text-red-600 dark:text-red-400"
              role="alert"
            >
              Passwords do not match.
            </p>
          </div>
        </div>

        <div v-if="mfaDisplay" class="space-y-4">
          <div>
            <label for="otp" class="block text-sm font-medium text-gray-900 dark:text-white"
              >Verification Code</label
            >
            <input
              id="otp"
              name="otp"
              type="text"
              inputmode="numeric"
              pattern="\d{6}"
              maxlength="6"
              autocomplete="one-time-code"
              required
              v-model="otp"
              :disabled="loading"
              class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500 dark:disabled:outline-white/5"
              placeholder="Enter 6-digit code"
            />
          </div>
        </div>

        <div class="pt-2 space-y-2">
          <button
            v-if="!mfaDisplay"
            type="submit"
            :disabled="!passwordsFormValid"
            @click="onContinue"
            class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
          >
            <span>Continue</span>
          </button>

          <div v-if="mfaDisplay" class="flex items-center gap-4">
            <button
              type="submit"
              :disabled="loading || !mfaFormValid"
              class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
            >
              <span v-if="loading">Updating…</span>
              <span v-else>Verify & Change</span>
            </button>
            <button
              type="button"
              :disabled="loading"
              @click="mfaDisplay = false"
              class="text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              Back
            </button>
          </div>

          <p v-if="auth.error" class="text-sm text-red-600 dark:text-red-400" role="alert">
            {{ auth.error }}
          </p>
          <p v-if="success" class="text-sm text-green-700 dark:text-green-400">
            Password updated successfully.
          </p>
        </div>
      </form>
    </div>

    </section>

    <PersonalAccessTokens />

    <ConnectedApps />

    <section id="billing" class="scroll-mt-20">
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white mt-4">Billing</h2>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        <template v-if="canManageBilling">Manage the subscription and billing details of {{ organizationName }}.</template>
        <template v-else>The subscription of {{ organizationName }} is managed by its owner.</template>
      </p>

      <div class="mt-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-gray-900/60 relative shadow-xs">
      <!-- Screen Loading Lock overlay -->
      <div v-if="loading" class="absolute inset-0 z-40 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
        <svg class="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <div v-if="subscription && (subscription.status === 'ACTIVE' || subscription.status === 'ON_HOLD')" class="space-y-6">
        <!-- Subscription Details Header -->
        <div class="flex items-start justify-between flex-wrap gap-4">
          <div class="space-y-1">
            <div class="flex items-center gap-3">
              <span class="inline-flex items-center px-3.5 py-1 rounded-full text-sm font-semibold"
                    :class="flavorBadge.classes">
                {{ flavorBadge.label }}
              </span>
              <span
                v-if="subscription.status !== 'ACTIVE'"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              >
                {{ formatEnum(subscription.status) }}
              </span>
            </div>
            <p v-if="formattedExpiresAt" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              <template v-if="subscription.cancelAtNextBillingDate">Expires on</template>
              <template v-else>Renews on</template>
              {{ formattedExpiresAt }}
            </p>
          </div>
        </div>

        <!-- Cancellation scheduled banner (ACTIVE only) -->
        <div
          v-if="subscription.cancelAtNextBillingDate"
          class="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20"
        >
          <div class="flex items-start gap-2">
            <svg class="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p class="text-sm text-amber-700 dark:text-amber-300">
              Your subscription is scheduled to cancel at the end of the current billing period. You'll retain access until then.
            </p>
          </div>
        </div>

        <!-- Pending Downgrade Banner -->
        <div
          v-if="subscription.pendingFlavor"
          class="p-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
        >
          <div class="flex items-start gap-2">
            <svg class="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            <p class="text-sm text-blue-800 dark:text-blue-300">
              Your plan is scheduled to downgrade to <span class="font-bold">{{ subscription.pendingFlavor }}</span> at the end of the current billing cycle. You'll retain access to your current features until then.
            </p>
          </div>
        </div>

        <!-- ON_HOLD payment warning -->
        <div
          v-if="subscription.status === 'ON_HOLD'"
          class="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20"
        >
          <div class="flex items-start gap-2">
            <svg class="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p class="text-sm text-amber-700 dark:text-amber-300">
              Your subscription is on hold due to a payment issue. Please update your payment method to continue using paid features.
            </p>
          </div>
        </div>

        <!-- Action buttons -->
        <div v-if="canManageBilling" class="flex flex-wrap gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-800 items-center">
          <button
            @click="handleManageSubscription"
            :disabled="loading"
            class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
          >
            Manage Subscription
          </button>

          <button
            v-if="subscription.flavor !== 'F_AND_F'"
            @click="handleUpdatePaymentMethod"
            :disabled="loading"
            class="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:ring-0 dark:hover:bg-white/20 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
          >
            Update Payment Method
          </button>

          <!-- Upsell to Pro -->
          <router-link
            v-if="subscription.flavor === 'BASIC' && !subscription.pendingFlavor"
            to="/billing"
            class="inline-flex items-center rounded-md bg-indigo-100 text-indigo-700 px-3 py-1.5 text-sm font-semibold shadow-sm hover:bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-900/60 cursor-pointer disabled:opacity-50"
          >
            Upgrade to Pro
          </router-link>

          <button
            v-if="downgradeTarget && subscription.status === 'ACTIVE' && !subscription.pendingFlavor"
            @click="showDowngradeModal = true"
            :disabled="loading"
            class="inline-flex items-center ml-auto text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white underline underline-offset-4 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
          >
            Downgrade to {{ formatEnum(downgradeTarget) }}
          </button>
        </div>

        <!-- Error message -->
        <p v-if="subscriptionStore.error" class="text-sm text-red-600 dark:text-red-400">
          {{ subscriptionStore.error }}
        </p>
      </div>
      <div v-else class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">No Active Plan</h3>
          <p v-if="canManageBilling" class="text-sm text-gray-500 dark:text-gray-400">Subscribe to unlock features.</p>
          <p v-else class="text-sm text-gray-500 dark:text-gray-400">Ask the organization owner to choose a plan.</p>
        </div>
        <router-link
          v-if="canManageBilling"
          to="/billing"
          class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          View Plans
        </router-link>
      </div>
    </div>

    <!-- Confirm Downgrade Modal -->
    <div v-if="showDowngradeModal" class="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-gray-100 dark:border-gray-700">
        <div class="mb-6">
          <div class="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Confirm Downgrade
          </h3>
          <p class="text-gray-600 dark:text-gray-300">
            You are downgrading to <strong>{{ downgradeTarget }}</strong>.
            <br/><br/>
            You will lose access to the features of your current plan that {{ formatEnum(downgradeTarget) }} does not include.
            Your existing plan access will continue until the end of your billing cycle.
          </p>

          <div v-if="subscription?.cancelAtNextBillingDate" class="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm border border-indigo-100 dark:border-indigo-800">
            <strong>Note:</strong> Your scheduled cancellation will be revoked and your subscription will be reactivated automatically!
          </div>
        </div>
        
        <div class="flex gap-4">
          <button
            @click="showDowngradeModal = false"
            class="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            @click="executeDowngrade"
            :disabled="loading"
            class="flex-1 py-2.5 px-4 font-semibold text-white rounded-xl transition-all shadow-sm bg-red-600 hover:bg-red-700 shadow-red-600/20 disabled:opacity-50 cursor-pointer disabled:cursor-wait"
          >
            {{ loading ? 'Processing...' : 'Confirm' }}
          </button>
        </div>
      </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';

import {useAuthStore} from '@/stores/auth';
import {type SubscriptionFlavor, useSubscriptionStore} from '@/stores/subscription';
import {useToastStore} from '@/stores/toast';
import {formatEnum} from '@/lib/format';
import gravatarUrl from '@/lib/gravatar';
import PersonalAccessTokens from '@/components/PersonalAccessTokens.vue';
import ConnectedApps from '@/components/ConnectedApps.vue';
import {useOrganizationStore} from '@/stores/organization';
import EmailVerificationForm from '@/components/EmailVerificationForm.vue';

const DOWNGRADE_TARGETS: Partial<Record<SubscriptionFlavor, SubscriptionFlavor>> = {
  ENTERPRISE: 'PRO',
  PRO: 'BASIC',
};

const auth = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const toast = useToastStore();
const organizations = useOrganizationStore();
const canManageBilling = computed(() => organizations.canManageBilling);
const organizationName = computed(() => organizations.current?.name ?? 'your organization');

// Step 1 fields
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');

// Step 2 field
const otp = ref('');

// UI state
const mfaDisplay = ref(false);
const success = ref(false);
const showDowngradeModal = ref(false);
const loading = computed(() => auth.loading || subscriptionStore.loading);
const subscription = computed(() => subscriptionStore.subscription);
const downgradeTarget = computed(() => {
  const flavor = subscription.value?.flavor;
  return flavor ? DOWNGRADE_TARGETS[flavor] ?? null : null;
});

const formattedExpiresAt = computed(() => {
  if (!subscription.value?.expiresAt) return null;
  const date = new Date(subscription.value.expiresAt);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
});

const flavorBadge = computed(() => {
  const flavor = subscription.value?.flavor;
  switch (flavor) {
    case 'PRO':
      return {
        label: 'Pro Plan',
        classes: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800',
      };
    case 'ENTERPRISE':
      return {
        label: 'Enterprise Plan',
        classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800',
      };
    case 'F_AND_F':
      return {
        label: 'Friends & Family Plan',
        classes: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800',
      };
    default:
      return {
        label: 'Basic Plan',
        classes: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700',
      };
  }
});

const passwordsMatch = computed(() => newPassword.value === confirmPassword.value);
const isPasswordValid = computed(() => checkPassword(newPassword.value));
const passwordsFormValid = computed(
  () =>
    Boolean(currentPassword.value && newPassword.value && confirmPassword.value) &&
    passwordsMatch.value &&
    isPasswordValid.value
);
const mfaFormValid = computed(
  () => otp.value.length === 6 && passwordsFormValid.value // Also re-check step 1
);

function checkPassword(password: string): boolean {
  if (!password) return false;
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  return hasMinLength && hasUppercase && hasLowercase && hasDigit && hasSymbol;
}

// --- Functions ---

onMounted(() => {
  subscriptionStore.fetchSubscription();
  auth.fetchMe().catch(() => {
    // The status badge stays hidden when /me is unavailable.
  });
});

async function handleManageSubscription() {
  try {
    const link = await subscriptionStore.getPortalLink();
    if (link) {
      window.location.href = link;
    }
  } catch (e) {
    if ((e as { response?: { status?: number } } | null)?.response?.status === 404) {
      subscriptionStore.error = 'Your billing account is not set up yet. Please complete a checkout first.';
    }
  }
}

async function handleUpdatePaymentMethod() {
  try {
    const link = await subscriptionStore.getUpdatePaymentMethodLink();
    if (link) {
      window.location.href = link;
    }
  } catch (e) {
    if ((e as { response?: { status?: number } } | null)?.response?.status === 404) {
      subscriptionStore.error = 'Your billing account is not set up yet. Please complete a checkout first.';
    }
    // Other errors already handled by store
  }
}

async function executeDowngrade() {
  if (!downgradeTarget.value) return;
  try {
    const result = await subscriptionStore.switchPlan(downgradeTarget.value);
    if (!result) return;
    showDowngradeModal.value = false;
    toast.show('Plan change scheduled for the end of your billing cycle.', 'success');
  } catch {
    showDowngradeModal.value = false;
  }
}

function onContinue() {
  if (!passwordsFormValid.value) return;
  // Passwords are valid, show the MFA field
  mfaDisplay.value = true;
  success.value = false; // Clear any previous success message
  auth.error = '';
}

function handleFormSubmit() {
  if (mfaDisplay.value) {
    onSubmit();
  } else {
    onContinue();
  }
}
async function onSubmit() {
  if (!mfaFormValid.value) return;
  success.value = false;
  try {
    await auth.verifyMfa(otp.value);
  } catch {
    return; // Stop execution
  }
  try {
    await auth.changePassword({
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
    });
    success.value = true;

    // Clear all fields for safety
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
    otp.value = '';
    mfaDisplay.value = false;
  } catch {
    mfaDisplay.value = false;
  }
}
</script>
